import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Box,
  CloseButton,
  Flex,
  Link,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  BuyerLegalType,
  UtilityActivity,
  FoaPart,
  Regulation,
  useUpdateOrganizationMutation,
  type EditOrganizationRequest,
  type OrgDto,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { isOwnerOrAdmin } from "../../../../../common/auth/utils";
import {
  coordinatorRegistry,
  useApiErrorEditorConfig,
  useCreateEditor,
  useEditor,
} from "../../../../../common/editing/save-coordinator";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { useIsV2 } from "../../../../../common/layout/v2/useIsV2";
import { useProcurementTerms } from "../../../../../common/terminology/useProcurementTerms";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../../../common/redux/reducers/authStateReducer";
import { useRunsPublicProcurements } from "../../../../../common/organization/useRunsPublicProcurements";
import { urls } from "../../../../../urls";
import { saveWithVersionConflictRetry } from "../saveWithVersionConflictRetry";
import { foaPartBanner, isOutsideFoa, regulationSupportsPublishing } from "../utils/foaPartDisplay";
import { REGULATION_OPTIONS } from "../utils/publicProcurementOptions";
import { ChangeRegimeButton } from "./ChangeRegimeButton";

interface Props {
  eventId: string;
}

/**
 * Everything the classification is and everything that follows from it lives here, so the buyer has
 * one place to look: the regime, the part, whichever input is still missing, and — when the value
 * has drifted away from the regime the procurement holds — what that means and how to act on it.
 */
export const ClassificationPanel = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const runsPublicProcurements = useRunsPublicProcurements();
  const terms = useProcurementTerms();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const [editOrg] = useUpdateOrganizationMutation();
  const companyProfileUrl = useIsV2() ? urls.v2.organizationProfileEdit : urls.myProfile.organization.edit;

  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const isClassified = state.eventContext.type !== "Unclassified";
  const isPrivate = state.eventContext.type === "Private";

  const canEditOrg = isOwnerOrAdmin(selectedOrg.roles);
  // The firmakort is a preview of what an unclassified draft will inherit, never a stand-in for what
  // a classified one holds: falling back there too would show a procurement a regulation it does not
  // have, and hide that it still needs one.
  const regulation = isClassified ? publicContext?.regulation ?? null : selectedOrg.defaultRegulation ?? null;
  const buyerLegalType = isClassified
    ? publicContext?.buyerLegalType ?? null
    : selectedOrg.buyerLegalType ?? null;
  const foaPart = publicContext?.foaPart ?? null;
  const estimatedValueNok =
    state.eventContext.estimatedValue?.value != null ? Number(state.eventContext.estimatedValue.value) : null;
  const banner = foaPartBanner({
    regulation,
    foaPart,
    estimatedValueNok,
    contractNature: state.eventContext.contractNature,
    buyerLegalType,
  });
  const isSavingRegulation = ["saving", "pending"].includes(fieldStatus("publicContext.regulation"));

  // EntitySaveCoordinator publishes the server's state and the settled status in one notification,
  // so once the save has settled the part on screen is the one the server derived.
  const [reclassification, setReclassification] = useState<{ from: FoaPart; to: FoaPart } | null>(null);
  const partBeforeRegulationChange = useRef<FoaPart | null>(null);
  useEffect(() => {
    const before = partBeforeRegulationChange.current;
    if (before == null || isSavingRegulation) return;
    partBeforeRegulationChange.current = null;
    if (foaPart == null || foaPart === before) return;
    setReclassification({ from: before, to: foaPart });
  }, [isSavingRegulation, foaPart]);

  const valueImpliesPrivate = !runsPublicProcurements || isOutsideFoa(estimatedValueNok);
  const valueSettled = !["saving", "pending"].includes(fieldStatus("eventContext.estimatedValue"));
  const valueDisagreesWithRegime =
    isClassified && valueSettled && estimatedValueNok != null && valueImpliesPrivate !== isPrivate;

  // The organisation has never answered the question, so the first procurement to ask it also
  // records the answer. Later changes are this procurement's alone — a buyer with a utility
  // activity switches per contract, not per organisation.
  const adoptsAsOrgDefault = selectedOrg.defaultRegulation == null && canEditOrg;
  // An unclassified draft can only write the organisation's default, so offering "Endre" there would
  // quietly move every future procurement. Overriding one procurement waits until it is classified.
  const canChangeRegulation = isClassified || adoptsAsOrgDefault;

  const editorErrorConfig = useApiErrorEditorConfig();
  useCreateEditor<OrgDto, EditOrganizationRequest>(selectedOrg.id, {
    initialState: selectedOrg,
    save: async (fields) => {
      await coordinatorRegistry.flush(eventId);
      const updatedOrg = await saveWithVersionConflictRetry({
        fields,
        save: (retryFields) =>
          editOrg({ orgId: selectedOrg.id, editOrganizationRequest: retryFields }).unwrap(),
        reloadLatest: () => coordinatorRegistry.flush(eventId),
      });
      dispatch(updateSelectedOrg(updatedOrg));
      return updatedOrg;
    },
    ...editorErrorConfig,
  });
  const { updateField: updateOrgField, fieldStatus: orgFieldStatus } = useEditor<OrgDto, EditOrganizationRequest>(
    selectedOrg.id,
  );

  /**
   * A classified procurement holds its own regulation, so the choice always lands there — writing
   * only the organisation default would leave the buyer looking at the value they just replaced.
   * An organisation that has never answered also gets the answer recorded as its default, and an
   * unclassified draft has nowhere else to put it, so there the default is the only write.
   */
  const chooseRegulation = (value: Regulation) => {
    if (!isClassified) {
      updateOrgField({ defaultRegulation: { value } });
      return;
    }
    setReclassification(null);
    partBeforeRegulationChange.current = value === regulation ? null : foaPart;
    updateField({ publicContext: { regulation: value } });
    if (adoptsAsOrgDefault) updateOrgField({ defaultRegulation: { value } });
  };

  const chooseBuyerLegalType = (value: BuyerLegalType) => {
    updateOrgField({ buyerLegalType: { value } });
  };

  const isSavingUtilityActivity = ["saving", "pending"].includes(fieldStatus("publicContext.utilityActivity"));

  const regimeLabel = isPrivate
    ? terms.simplifiedRegime
    : isClassified
      ? t("Public procurement")
      : t("Not classified yet");
  const partLabel = isPrivate ? null : foaPart ? t(foaPart) : isClassified ? t("Not classified yet") : null;

  return (
    <Box mt="6" borderWidth="1px" borderRadius="md" p="4">
      <Flex align="baseline" gap="2" wrap="wrap">
        <Text fontSize="sm" color="smMuted">
          {t("Classification")}
        </Text>
        <Text fontSize="md" fontWeight="semibold">
          {regimeLabel}
        </Text>
        {partLabel && (
          <>
            <Text fontSize="md" color="smMuted">
              ·
            </Text>
            <Text fontSize="md" fontWeight="semibold">
              {partLabel}
            </Text>
          </>
        )}
      </Flex>
      {!isPrivate && (
        <Text fontSize="xs" color="smMuted" mt="1">
          {banner
            ? t(banner.messageKey, banner.values ?? {})
            : t("Set the estimated value and the contract type to classify the procurement")}
        </Text>
      )}

      {reclassification && (
        <Alert status="info" mt="3" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box flex="1">
            {t("The change of regulation moved this procurement from {{from}} to {{to}}", {
              from: t(reclassification.from),
              to: t(reclassification.to),
            })}
          </Box>
          <CloseButton size="sm" onClick={() => setReclassification(null)} />
        </Alert>
      )}

      {valueDisagreesWithRegime && (
        <Alert status="warning" mt="3" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box>
            {isPrivate
              ? terms.valueAboveSimplifiedThreshold
              : t("The estimated value is below 500 000 NOK, but this procurement is already registered as public")}
            <Text fontSize="xs" color="smMuted" mt="1">
              {t("The classification is not changed automatically — changing it discards the work done under the current one")}
            </Text>
            <ChangeRegimeButton eventId={eventId} currentRegime={isPrivate ? "Private" : "Public"} />
          </Box>
        </Alert>
      )}

      {!isPrivate && (
        <>
          {canChangeRegulation ? (
            <Accordion allowToggle defaultIndex={regulation == null ? 0 : undefined} mt="3">
              <AccordionItem border="none">
                <AccordionButton px="0" py="2">
                  <Flex flex="1" align="baseline" gap="2" wrap="wrap" textAlign="left">
                    <Text fontSize="xs" color="smMuted">
                      {t("Governing regulation")}
                    </Text>
                    <Text fontSize="xs" fontWeight="medium">
                      {regulation ? t(regulation) : t("Not set")}
                    </Text>
                    {isSavingRegulation && <Spinner size="xs" />}
                  </Flex>
                  <Text fontSize="xs" color="smPrimary" mr="1">
                    {t("Change regulation")}
                  </Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px="0" pb="3">
                  <RadioOptionGroup
                    options={REGULATION_OPTIONS}
                    value={regulation ?? ""}
                    onChange={(value) => chooseRegulation(value as Regulation)}
                  />
                  <Text fontSize="xs" color="smMuted" mt="2">
                    {t(
                      "Which regulation applies follows from what the procurement is for, not from the kind of body you are — the same organisation can have procurements under both",
                    )}
                  </Text>
                  {(adoptsAsOrgDefault || !isClassified) && (
                    <Text fontSize="xs" color="smMuted" mt="2">
                      {t("Your first choice is saved as the default for the whole organisation")}
                    </Text>
                  )}
                  {!regulationSupportsPublishing(regulation) && (
                    <Alert status="info" mt="3" borderRadius="md" fontSize="sm">
                      <AlertIcon />
                      {t("You can register the procurement under this regulation, but announcing it is not supported yet")}
                    </Alert>
                  )}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          ) : (
            <Flex align="baseline" gap="2" wrap="wrap" mt="3" py="2">
              <Text fontSize="xs" color="smMuted">
                {t("Governing regulation")}
              </Text>
              <Text fontSize="xs" fontWeight="medium">
                {regulation ? t(regulation) : t("Not set")}
              </Text>
              <Text fontSize="xs" color="smMuted">
                {t("Can be changed per procurement once the procurement is classified")}
              </Text>
            </Flex>
          )}

          {isClassified && regulation === Regulation.Forsyningsforskriften && (
            <Box pt="3" borderTopWidth="1px" mt="3">
              <Flex align="baseline" gap="2" wrap="wrap">
                <Text fontSize="xs" color="smMuted">
                  {t("Utility activity")}
                </Text>
                <Text fontSize="xs" fontWeight="medium">
                  {publicContext?.utilityActivity ? t(publicContext.utilityActivity) : t("Not set")}
                </Text>
                {isSavingUtilityActivity && <Spinner size="xs" />}
              </Flex>
              <Box mt="2">
                <Select
                  size="sm"
                  maxW="sm"
                  value={publicContext?.utilityActivity ?? ""}
                  placeholder={t("Select the utility activity the procurement serves") ?? ""}
                  onChange={(e) =>
                    e.target.value &&
                    updateField({ publicContext: { utilityActivity: e.target.value as UtilityActivity } })
                  }
                >
                  {Object.values(UtilityActivity).map((activity) => (
                    <option key={activity} value={activity}>
                      {t(activity)}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Announced on the notice as the activity of the contracting entity")}
                </Text>
              </Box>
            </Box>
          )}

          <Box pt="3" borderTopWidth="1px">
            <Flex align="baseline" gap="2" wrap="wrap">
              <Text fontSize="xs" color="smMuted">
                {t("Buyer legal type")}
              </Text>
              <Text fontSize="xs" fontWeight="medium">
                {buyerLegalType ? t(buyerLegalType) : t("Not set")}
              </Text>
              {["saving", "pending"].includes(orgFieldStatus("buyerLegalType")) && <Spinner size="xs" />}
              {buyerLegalType && me.isSystemAdmin && (
                <Link as={RouterLink} to={companyProfileUrl} fontSize="xs" color="smPrimary">
                  {t("Edit in the company profile")}
                </Link>
              )}
            </Flex>
            {!buyerLegalType && me.isSystemAdmin && (
              <Box mt="2">
                <Select
                  size="sm"
                  maxW="sm"
                  value=""
                  aria-label={t("Buyer legal type") ?? undefined}
                  placeholder={t("Select the type of body you are") ?? ""}
                  onChange={(e) => e.target.value && chooseBuyerLegalType(e.target.value as BuyerLegalType)}
                >
                  {Object.values(BuyerLegalType).map((type) => (
                    <option key={type} value={type}>
                      {t(type)}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Applies to the whole organisation — it is part of the company profile")}
                </Text>
              </Box>
            )}
            {!buyerLegalType && !me.isSystemAdmin && (
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("The buyer legal type is managed by SourceMagnet — contact us to have it set")}
              </Text>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
